import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ApiOutlined,
  AppstoreOutlined,
  ApartmentOutlined,
  DeleteOutlined,
  DownOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  EditOutlined,
  FormOutlined,
  HomeOutlined,
  LineChartOutlined,
  LoadingOutlined,
  MessageOutlined,
  MoreOutlined,
  PlusCircleFilled,
  ReadOutlined,
  SearchOutlined,
  SettingOutlined,
  SwapOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import classNames from "classnames";

import logo from "../../../assets/logo.svg";
import LoadingSkeleton from "../../../components/CustomComponents/LoadingSkeleton";
import { useAppSelector } from "../../../hooks/redux";
import type { NeoConversationActivityStatus } from "../../../hexagon/core/ports/driven/neoConversationRepository";
import {
  canAccessDeviceOrders,
  hasProjectNeoAccess,
} from "../../../util/subdomain";
import LoadingCircle from "../../neo/LoadingCircle";
import { NeoSparkIcon } from "../../neo/NeoSparkIcon";
import { useNeoDrawer } from "../../neo/NeoDrawer/NeoDrawerContext";
import { getMoreNavigationItems } from "../appNavigationRegistry";
import { isCompassusExperience } from "../../../util/isCompassusUser";
import FacilitySwitcherModal from "./FacilitySwitcherModal";
import QuickSearchModal from "./QuickSearchModal";
import RecentConversationTitle from "./RecentConversationTitle";
import {
  ASK_FOR_MORE_CREDITS_LABEL,
  openAskForMoreCreditsChat,
} from "../../../util/askForMoreCredits";
import {
  formatSpend,
  formatSpendDollars,
  formatSpendWhole,
  useMySpend,
} from "./useMySpend";
import { useRecentConversations } from "./useRecentConversations";
import styles from "./styles.module.scss";

interface NavigationButtonProps {
  active: boolean;
  collapsed: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

const NavigationButton = ({
  active,
  collapsed,
  icon,
  label,
  onClick,
}: NavigationButtonProps) => (
  <button
    type="button"
    className={classNames(styles.navigationItem, {
      [styles.navigationItemActive]: active,
      [styles.navigationItemCollapsed]: collapsed,
    })}
    onClick={onClick}
    title={collapsed ? label : undefined}
    aria-label={label}
  >
    <span className={styles.navigationIcon}>{icon}</span>
    {!collapsed && <span className={styles.navigationLabel}>{label}</span>}
  </button>
);

/** The user's initials for avatars (shared with the compact shell's top bar). */
export const initials = (firstName?: string, lastName?: string): string =>
  `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() || "U";

const RECENT_CHAT_BATCH_SIZE = 5;
const ACTIVITY_STATUS_LABELS: Record<NeoConversationActivityStatus, string> = {
  streaming: "Generating",
  finished_unviewed: "Finished",
  idle: "Idle",
};

const RecentActivityDot = ({
  status,
  labelled = true,
}: {
  status: NeoConversationActivityStatus;
  labelled?: boolean;
}) => (
  <span
    className={styles.recentStatusSlot}
    aria-label={labelled ? ACTIVITY_STATUS_LABELS[status] : undefined}
    aria-hidden={!labelled}
    title={labelled ? ACTIVITY_STATUS_LABELS[status] : undefined}
  >
    {status === "streaming" ? (
      <LoadingCircle className={styles.recentStatusSpinner} />
    ) : (
      <span
        className={classNames(
          styles.recentStatusDot,
          styles[`recentStatusDot_${status}`]
        )}
        aria-hidden
      />
    )}
  </span>
);

interface AppNavigationProps {
  onLogout: () => void;
  /** ``drawer``: rendered inside the phone/tablet overlay drawer — always
   *  expanded, with a close button instead of collapse, and it asks to close
   *  whenever the user navigates. Defaults to the persistent sidebar. */
  variant?: "sidebar" | "drawer";
  onRequestClose?: () => void;
}

// Loaded on first open: it pulls in every settings section's controllers.
const SettingsModal = lazy(() => import("../../components/SettingsModal"));
// Type only: the modal itself stays lazy.
type SettingsSectionKey =
  import("../../components/SettingsModal").SettingsSectionKey;

// Temporarily hidden from the UI (credits/usage panel). Flip to `true` to bring
// the sidebar credit card back; the implementation below is intentionally kept.
const SHOW_CREDIT_CARD = false;

const AppNavigation = ({
  onLogout,
  variant = "sidebar",
  onRequestClose,
}: AppNavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDrawer = variant === "drawer";
  const [collapsedState, setCollapsed] = useState(false);
  // The overlay drawer has room for labels, so it never collapses to icons.
  const collapsed = !isDrawer && collapsedState;

  // In the drawer, any navigation (a nav item, a recent chat, a facility
  // switch) closes it so the user lands on the page they picked.
  const routeKey = `${location.pathname}${location.search}`;
  const lastRouteKeyRef = useRef(routeKey);
  useEffect(() => {
    if (lastRouteKeyRef.current === routeKey) return;
    lastRouteKeyRef.current = routeKey;
    if (isDrawer) onRequestClose?.();
  }, [routeKey, isDrawer, onRequestClose]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(true);
  const [creditOpen, setCreditOpen] = useState(false);
  const [recentVisibleCount, setRecentVisibleCount] = useState(
    RECENT_CHAT_BATCH_SIZE
  );
  const [recentOptionsId, setRecentOptionsId] = useState<number | null>(null);
  const [renamingConversation, setRenamingConversation] = useState<{
    id: number;
    title: string;
  } | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [facilityOpen, setFacilityOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState<
    SettingsSectionKey | undefined
  >(undefined);
  const openSettings = (section?: SettingsSectionKey) => {
    setSettingsSection(section);
    setSettingsOpen(true);
  };
  const creditPopoverRef = useRef<HTMLDivElement>(null);
  // Only fetch spend when the credit panel is actually shown (see
  // SHOW_CREDIT_CARD); otherwise we'd request data nobody will see.
  const { loading: spendLoading, spend } = useMySpend({
    enabled: SHOW_CREDIT_CARD,
  });

  const { user, clinic, clinicList, betaFeatures } = useAppSelector(
    (state) => ({
      user: state.auth.user,
      clinic: state.clinic.userClinic,
      clinicList: state.clinic.clinicList ?? [],
      betaFeatures: state.auth.betaFeaturesAllowedList ?? [],
    })
  );

  const neoDrawer = useNeoDrawer();
  const openIntegrations = () => navigate("/onboarding?step=notifications");
  const showNeo = hasProjectNeoAccess({
    email: user?.email,
    betaFeaturesAllowedList: betaFeatures,
  });
  const {
    conversations: recentConversations,
    deleteConversation,
    deletingId,
    loading: recentLoading,
    markConversationViewed,
    renameConversation,
  } = useRecentConversations(showNeo);
  const visibleRecentConversations = recentConversations.slice(
    0,
    recentVisibleCount
  );
  const activeConversationId =
    location.pathname === "/copilot"
      ? new URLSearchParams(location.search).get("conversation")
      : null;
  const nextRecentConversation = recentConversations[recentVisibleCount];
  const hasMoreRecentConversations =
    recentVisibleCount < recentConversations.length;
  const recentChatsExpanded =
    recentVisibleCount > RECENT_CHAT_BATCH_SIZE && !hasMoreRecentConversations;
  const moreItems = useMemo(
    () =>
      getMoreNavigationItems({
        groups: user?.groups ?? [],
        isNursePractitioner: Boolean(user?.isNursePractitioner),
        // Loose ``!=`` like MobileLayout: ehr_id may arrive as "0".
        clinicHasEhr: clinic?.ehr_id != 0,
        hasDeviceOrdersAccess: canAccessDeviceOrders(clinic?.id),
        isCompassus: isCompassusExperience({ user, userClinic: clinic }),
      }),
    [user, clinic]
  );
  const moreActive = moreItems.some(
    (item) =>
      location.pathname === item.key ||
      location.pathname.startsWith(`${item.key}/`)
  );
  const monthProgress = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime();
    return ((now.getTime() - start) / (end - start)) * 100;
  }, []);
  // Credits fill: only defined when enforcement gives us a cap (denominator).
  const creditsPercent =
    spend?.enforced && spend.denominator != null
      ? spend.denominator > 0
        ? (spend.numerator / spend.denominator) * 100
        : spend.numerator > 0
        ? 100
        : 0
      : null;
  // Trial fill: current day / total, for users in an active free trial.
  const trialPercent =
    spend?.trialDaysTotal != null &&
    spend.trialDaysTotal > 0 &&
    spend.trialDayNumber != null
      ? (spend.trialDayNumber / spend.trialDaysTotal) * 100
      : null;
  // Show the higher of the credits and trial-days fractions. Fall back to the
  // month-progress fill when neither is available (non-trial, unenforced).
  const barPercent =
    creditsPercent != null || trialPercent != null
      ? Math.max(creditsPercent ?? 0, trialPercent ?? 0)
      : monthProgress;
  const remainingCredits =
    spend?.denominator != null
      ? Math.max(spend.denominator - spend.numerator, 0)
      : null;
  // "n / m days" trial label (null unless in an active trial).
  const trialDaysLabel =
    spend?.trialDaysTotal != null && spend.trialDayNumber != null
      ? `${spend.trialDayNumber} / ${spend.trialDaysTotal} days`
      : null;

  useEffect(() => {
    const conversationId = Number(activeConversationId);
    if (!Number.isInteger(conversationId) || conversationId <= 0) return;
    const conversation = recentConversations.find(
      (candidate) => candidate.id === conversationId
    );
    const latestSequenceNum = conversation?.latest_message_sequence_num;
    if (
      conversation?.activity_status !== "finished_unviewed" ||
      typeof latestSequenceNum !== "number" ||
      !Number.isInteger(latestSequenceNum) ||
      latestSequenceNum <= 0
    ) {
      return;
    }
    void markConversationViewed(conversationId, latestSequenceNum);
  }, [activeConversationId, markConversationViewed, recentConversations]);

  useEffect(() => {
    if (!creditOpen) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !creditPopoverRef.current?.contains(event.target)
      ) {
        setCreditOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setCreditOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [creditOpen]);

  const openMore = () => {
    if (collapsed) setCollapsed(false);
    setMoreOpen((open) => !open);
  };

  const switchFacility = (clinicId: number) => {
    const search = new URLSearchParams(location.search);
    search.set("clinic_id", String(clinicId));
    navigate({ pathname: location.pathname, search: search.toString() });
  };

  const deleteRecentConversation = async (conversationId: number) => {
    const deleted = await deleteConversation(conversationId);
    if (
      deleted &&
      location.pathname === "/copilot" &&
      new URLSearchParams(location.search).get("conversation") ===
        String(conversationId)
    ) {
      navigate(`/copilot?new=deleted-${conversationId}`, { replace: true });
    }
  };

  const beginRecentConversationRename = (
    conversationId: number,
    title: string
  ) => {
    setRecentOptionsId(null);
    setRenamingConversation({ id: conversationId, title });
  };

  const commitRecentConversationRename = async () => {
    if (!renamingConversation) return;
    const current = renamingConversation;
    if (!current.title.trim()) return;
    if (await renameConversation(current.id, current.title)) {
      setRenamingConversation(null);
    }
  };

  const facilitySwitcher = (
    <button
      type="button"
      className={styles.facilityButton}
      aria-label="Switch facility"
      onClick={() => setFacilityOpen(true)}
    >
      {!collapsed && (
        <span className={styles.clinicName}>{clinic?.name || "Facility"}</span>
      )}
      <SwapOutlined className={styles.facilitySwapIcon} />
    </button>
  );

  return (
    <aside
      className={classNames(styles.sidebar, {
        [styles.sidebarCollapsed]: collapsed,
        [styles.sidebarDrawer]: isDrawer,
      })}
    >
      <div className={styles.header}>
        {isDrawer ? (
          <>
            <img className={styles.logo} src={logo} alt="Circadia" />
            <button
              type="button"
              className={styles.collapseButton}
              aria-label="Close navigation"
              onClick={onRequestClose}
            >
              {/* Same collapse icon as the desktop sidebar. */}
              <DoubleLeftOutlined />
            </button>
          </>
        ) : collapsed ? (
          <button
            type="button"
            className={styles.collapsedBrandButton}
            aria-label="Expand navigation"
            onClick={() => setCollapsed(false)}
          >
            <img
              className={styles.collapsedBrandLogo}
              src={logo}
              alt=""
              aria-hidden
            />
            <DoubleRightOutlined className={styles.collapsedExpandIcon} />
          </button>
        ) : (
          <>
            <img className={styles.logo} src={logo} alt="Circadia" />
            <button
              type="button"
              className={styles.collapseButton}
              aria-label="Collapse navigation"
              onClick={() => setCollapsed(true)}
            >
              <DoubleLeftOutlined />
            </button>
          </>
        )}
      </div>

      <button
        type="button"
        className={classNames(styles.searchButton, {
          [styles.searchButtonCollapsed]: collapsed,
        })}
        onClick={() => setSearchOpen(true)}
        aria-label="Search patients"
      >
        <SearchOutlined className={styles.searchIcon} />
        {!collapsed && <span>Search</span>}
      </button>

      <nav className={styles.navigation} aria-label="Main navigation">
        {showNeo && (
          <>
            <NavigationButton
              active={
                location.pathname === "/copilot" &&
                !new URLSearchParams(location.search).has("conversation")
              }
              collapsed={collapsed}
              icon={<PlusCircleFilled className={styles.newChatIcon} />}
              label="New Chat"
              onClick={() => navigate(`/copilot?new=${Date.now()}`)}
            />
            <NavigationButton
              active={location.pathname === "/home"}
              collapsed={collapsed}
              icon={<HomeOutlined />}
              label="Home"
              onClick={() => navigate("/home")}
            />
          </>
        )}
        <NavigationButton
          active={location.pathname === "/predict"}
          collapsed={collapsed}
          icon={<LineChartOutlined />}
          label="Predict"
          onClick={() => navigate("/predict")}
        />
        {showNeo && (
          <>
            <NavigationButton
              active={location.pathname === "/templates"}
              collapsed={collapsed}
              icon={<AppstoreOutlined />}
              label="Templates"
              onClick={() => navigate("/templates")}
            />
            <NavigationButton
              active={location.pathname === "/workflows"}
              collapsed={collapsed}
              icon={<ApartmentOutlined />}
              label="Workflows"
              onClick={() => navigate("/workflows")}
            />
          </>
        )}
        {collapsed && showNeo && (
          // Collapsed: Recent Tasks keeps an icon; it expands the sidebar
          // with the list open (like Tools).
          <NavigationButton
            active={
              location.pathname === "/copilot" &&
              new URLSearchParams(location.search).has("conversation")
            }
            collapsed
            icon={<MessageOutlined />}
            label="Recent Tasks"
            onClick={() => {
              setCollapsed(false);
              setRecentOpen(true);
            }}
          />
        )}
        {!collapsed && showNeo && (
          <section className={styles.recentSection}>
            <div className={styles.recentHeader}>
              <button
                type="button"
                className={styles.recentToggle}
                onClick={() => setRecentOpen((open) => !open)}
                aria-expanded={recentOpen}
                aria-label="Recent Tasks"
              >
                <MessageOutlined className={styles.recentSectionIcon} />
                <span>Recent Tasks</span>
              </button>
              <button
                type="button"
                className={styles.recentOpenAll}
                onClick={() => navigate(`/copilot?new=${Date.now()}`)}
                aria-label="New conversation"
                title="New conversation"
              >
                <FormOutlined />
              </button>
              <button
                type="button"
                className={styles.recentChevronButton}
                onClick={() => setRecentOpen((open) => !open)}
                aria-expanded={recentOpen}
                aria-label={
                  recentOpen ? "Collapse recent chats" : "Expand recent chats"
                }
              >
                <DownOutlined
                  className={classNames(styles.recentChevron, {
                    [styles.recentChevronCollapsed]: !recentOpen,
                  })}
                />
              </button>
            </div>
            {recentOpen && (
              <div className={styles.recentList}>
                {recentLoading && recentConversations.length === 0 ? (
                  <LoadingSkeleton
                    ariaLabel="Loading recent chats"
                    className={styles.recentSkeleton}
                    compact
                    rows={3}
                  />
                ) : recentConversations.length === 0 ? (
                  <span className={styles.recentEmpty}>
                    No conversations yet
                  </span>
                ) : (
                  <>
                    {visibleRecentConversations.map((conversation) => {
                      const title =
                        conversation.title?.trim() || "Untitled conversation";
                      const activityStatus =
                        conversation.activity_status ?? "idle";
                      const isRenaming =
                        renamingConversation?.id === conversation.id;
                      return (
                        <div
                          className={styles.recentItemRow}
                          key={conversation.id}
                        >
                          {isRenaming ? (
                            <form
                              className={classNames(
                                styles.recentItem,
                                styles.recentRenameForm,
                                {
                                  [styles.recentItemActive]:
                                    activeConversationId ===
                                    String(conversation.id),
                                }
                              )}
                              onSubmit={(event) => {
                                event.preventDefault();
                                void commitRecentConversationRename();
                              }}
                            >
                              <RecentActivityDot status={activityStatus} />
                              <input
                                value={renamingConversation.title}
                                onChange={(event) =>
                                  setRenamingConversation({
                                    id: conversation.id,
                                    title: event.target.value,
                                  })
                                }
                                onBlur={() => setRenamingConversation(null)}
                                onKeyDown={(event) => {
                                  if (event.key === "Escape") {
                                    event.preventDefault();
                                    setRenamingConversation(null);
                                  }
                                }}
                                aria-label={`Rename ${title}`}
                                maxLength={255}
                                autoFocus
                              />
                            </form>
                          ) : (
                            <button
                              type="button"
                              className={classNames(styles.recentItem, {
                                [styles.recentItemActive]:
                                  activeConversationId ===
                                  String(conversation.id),
                              })}
                              onClick={() =>
                                navigate(
                                  `/copilot?conversation=${conversation.id}`
                                )
                              }
                              title={title}
                              aria-label={title}
                            >
                              <RecentActivityDot status={activityStatus} />
                              <RecentConversationTitle title={title} />
                            </button>
                          )}
                          {!isRenaming && (
                            <div
                              className={styles.recentOptions}
                              onBlur={(event) => {
                                if (
                                  !event.currentTarget.contains(
                                    event.relatedTarget
                                  )
                                ) {
                                  setRecentOptionsId(null);
                                }
                              }}
                            >
                              <button
                                type="button"
                                className={styles.recentMoreButton}
                                onClick={() =>
                                  setRecentOptionsId((current) =>
                                    current === conversation.id
                                      ? null
                                      : conversation.id
                                  )
                                }
                                disabled={deletingId !== null}
                                aria-label={`More options for ${title}`}
                                aria-expanded={
                                  recentOptionsId === conversation.id
                                }
                                aria-haspopup="menu"
                              >
                                {deletingId === conversation.id ? (
                                  <LoadingOutlined spin />
                                ) : (
                                  <MoreOutlined />
                                )}
                              </button>
                              {recentOptionsId === conversation.id && (
                                <div
                                  className={styles.recentOptionsMenu}
                                  role="menu"
                                >
                                  <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() =>
                                      beginRecentConversationRename(
                                        conversation.id,
                                        title
                                      )
                                    }
                                  >
                                    <EditOutlined />
                                    <span>Rename</span>
                                  </button>
                                  <button
                                    type="button"
                                    role="menuitem"
                                    className={styles.recentOptionsDelete}
                                    onClick={() => {
                                      setRecentOptionsId(null);
                                      void deleteRecentConversation(
                                        conversation.id
                                      );
                                    }}
                                  >
                                    <DeleteOutlined />
                                    <span>Delete</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {nextRecentConversation && (
                      <div className={styles.recentPreview} aria-hidden>
                        <div className={styles.recentItem}>
                          <RecentActivityDot
                            status={
                              nextRecentConversation.activity_status ?? "idle"
                            }
                            labelled={false}
                          />
                          <RecentConversationTitle
                            title={
                              nextRecentConversation.title?.trim() ||
                              "Untitled conversation"
                            }
                          />
                        </div>
                        <span className={styles.recentFade} />
                      </div>
                    )}
                    {(hasMoreRecentConversations || recentChatsExpanded) && (
                      <button
                        type="button"
                        className={styles.recentLoadMore}
                        aria-label={
                          recentChatsExpanded ? "Show less" : "Show more"
                        }
                        onClick={() =>
                          setRecentVisibleCount((count) =>
                            hasMoreRecentConversations
                              ? Math.min(
                                  recentConversations.length,
                                  count + RECENT_CHAT_BATCH_SIZE
                                )
                              : RECENT_CHAT_BATCH_SIZE
                          )
                        }
                      >
                        <span>
                          {recentChatsExpanded ? "Show less" : "Show more"}
                        </span>
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </section>
        )}
        <button
          type="button"
          className={classNames(styles.showMoreButton, {
            [styles.showMoreButtonActive]: moreActive,
            [styles.navigationItemCollapsed]: collapsed,
          })}
          onClick={openMore}
          aria-expanded={moreOpen}
          aria-label="Tools"
          title={collapsed ? "Tools" : undefined}
        >
          <ToolOutlined className={styles.toolsIcon} />
          {!collapsed && (
            <>
              <span className={styles.showMoreText}>Tools</span>
              <DownOutlined
                className={classNames(styles.showMoreIcon, {
                  [styles.showMoreIconRotated]: !moreOpen,
                })}
              />
            </>
          )}
        </button>

        {!collapsed && moreOpen && (
          <div className={styles.moreNavigation}>
            {moreItems.map((item) => (
              <NavigationButton
                key={item.key}
                active={
                  location.pathname === item.key ||
                  location.pathname.startsWith(`${item.key}/`)
                }
                collapsed={false}
                icon={item.icon}
                label={item.label}
                onClick={() => navigate(item.key)}
              />
            ))}
          </div>
        )}
      </nav>

      <div className={styles.utilities}>
        {SHOW_CREDIT_CARD && !collapsed && (
          <div className={styles.creditCard} ref={creditPopoverRef}>
            <button
              type="button"
              className={styles.creditTrigger}
              onClick={() => setCreditOpen((open) => !open)}
              aria-expanded={creditOpen}
              aria-haspopup="dialog"
              aria-controls="credit-usage-popover"
              aria-label="View credit details"
            >
              <span className={styles.creditAmount}>
                {spendLoading || !spend
                  ? "—"
                  : spend.denominator != null
                  ? `${formatSpend(spend.numerator)} / ${formatSpendWhole(
                      spend.denominator
                    )} credits spent`
                  : `${formatSpend(spend.numerator)} credits spent`}
              </span>
            </button>
            {!spendLoading && trialDaysLabel && (
              <span className={styles.creditTrialDays}>{trialDaysLabel}</span>
            )}
            <div className={styles.creditBar} aria-hidden>
              <span
                className={styles.creditBarFill}
                style={{
                  width: `${Math.min(100, Math.max(0, barPercent))}%`,
                }}
              />
            </div>
            {creditOpen && (
              <div
                id="credit-usage-popover"
                className={styles.creditPopover}
                role="dialog"
                aria-label="Credit details"
              >
                <div className={styles.creditPopoverHeader}>
                  <strong>Usage this month</strong>
                </div>
                {spend ? (
                  <>
                    <div className={styles.creditPopoverSummary}>
                      {spend.denominator != null
                        ? `${formatSpend(spend.numerator)} / ${formatSpendWhole(
                            spend.denominator
                          )} credits spent`
                        : `${formatSpend(spend.numerator)} credits spent`}
                    </div>
                    <div
                      className={styles.creditPopoverBar}
                      aria-label={`${Math.round(barPercent)}% of credits used`}
                    >
                      <span
                        style={{
                          width: `${Math.min(100, Math.max(0, barPercent))}%`,
                        }}
                      />
                    </div>
                    <dl className={styles.creditDetails}>
                      <div>
                        <dt>Workflows</dt>
                        <dd>{formatSpendDollars(spend.workflows)}</dd>
                      </div>
                      <div>
                        <dt>Chats</dt>
                        <dd>{formatSpendDollars(spend.interactive)}</dd>
                      </div>
                      {remainingCredits != null && (
                        <div className={styles.creditRemaining}>
                          <dt>Remaining</dt>
                          <dd>{formatSpend(remainingCredits)}</dd>
                        </div>
                      )}
                      {trialDaysLabel && (
                        <div className={styles.creditTrial}>
                          <dt>Trial</dt>
                          <dd>{trialDaysLabel}</dd>
                        </div>
                      )}
                    </dl>
                  </>
                ) : (
                  <p className={styles.creditUnavailable}>
                    Credit details are temporarily unavailable.
                  </p>
                )}
                <button
                  type="button"
                  className={styles.askCreditsButton}
                  onClick={() => {
                    setCreditOpen(false);
                    openAskForMoreCreditsChat();
                  }}
                >
                  {ASK_FOR_MORE_CREDITS_LABEL}
                </button>
              </div>
            )}
          </div>
        )}
        {/* In the compact shell's drawer, "Ask Circadia AI" lives in the top
            bar instead. */}
        {showNeo && neoDrawer && !isDrawer ? (
          <NavigationButton
            active={neoDrawer.open}
            collapsed={collapsed}
            icon={<NeoSparkIcon size={16} />}
            label="Ask Circadia AI"
            onClick={() =>
              neoDrawer.open ? neoDrawer.close() : neoDrawer.openNewChat()
            }
          />
        ) : (
          <NavigationButton
            active={location.pathname === "/onboarding"}
            collapsed={collapsed}
            icon={<ApiOutlined />}
            label="Integrations"
            onClick={openIntegrations}
          />
        )}
        <NavigationButton
          active={false}
          collapsed={collapsed}
          icon={<ReadOutlined />}
          label="Learn"
          onClick={() =>
            window.open("https://help.circadia.health", "_blank", "noopener")
          }
        />
        <NavigationButton
          active={settingsOpen}
          collapsed={collapsed}
          icon={<SettingOutlined />}
          label="Settings"
          onClick={() => openSettings()}
        />
      </div>

      <div className={styles.accountRow}>
        {/* Your profile opens Settings on Account (Log out lives there). */}
        <button
          type="button"
          className={styles.profileButton}
          aria-label="Open account settings"
          onClick={() => openSettings("account")}
        >
          <span className={styles.avatar}>
            {initials(user?.firstName, user?.lastName)}
          </span>
        </button>
        {!collapsed ? (
          <span className={styles.accountDetails}>
            <button
              type="button"
              className={styles.accountName}
              onClick={() => openSettings("account")}
            >
              {user?.firstName || user?.email || "User"}
            </button>
            {facilitySwitcher}
          </span>
        ) : (
          facilitySwitcher
        )}
      </div>
      <QuickSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
      {settingsOpen && (
        <Suspense fallback={null}>
          <SettingsModal
            open
            onClose={() => setSettingsOpen(false)}
            onLogout={onLogout}
            initialSection={settingsSection}
          />
        </Suspense>
      )}
      <FacilitySwitcherModal
        open={facilityOpen}
        onClose={() => setFacilityOpen(false)}
        clinics={clinicList}
        selectedClinicId={clinic?.id}
        onSelect={switchFacility}
      />
    </aside>
  );
};

export default AppNavigation;
